import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { applySeo } from "@/lib/seo";
import { trpc } from "@/lib/trpc";
import { BarometerNav } from "@/components/BarometerNav";

// Produkt-ID → Name + Barometer-URL Mapping
const PRODUCT_MAP: Record<string, { name: string; url: string; emoji: string }> = {
  sensorik_analyse:      { name: "Sensorik-Barometer™",       url: "/sensorik",     emoji: "/manus-storage/11_094992a3.svg" },
  lokal_analyse:         { name: "Lokal-Barometer™",           url: "/lokal",        emoji: "/manus-storage/1_55991aa3.svg" },
  geo_analyse:           { name: "GEO-Barometer™",             url: "/geo-barometer",emoji: "/manus-storage/7_8bcad2d2.svg" },
  seo_analyse:           { name: "SEO-Barometer™",             url: "/seo",          emoji: "/manus-storage/23_1ab7147e.svg" },
  conversion_analyse:    { name: "Conversion-Barometer™",      url: "/conversion",   emoji: "/manus-storage/33_d51b7819.svg" },
  accessibility_analyse: { name: "Accessibility-Barometer™",   url: "/accessibility",emoji: "/manus-storage/16_9c3f7718.svg" },
  datenschutz_analyse:   { name: "Datenschutz-Barometer™",     url: "/datenschutz",  emoji: "/manus-storage/29_7f3bb394.svg" },
  ecommerce_analyse:     { name: "E-Commerce-Barometer™",      url: "/ecommerce",    emoji: "/manus-storage/21_453003f0.svg" },
  wettbewerb_analyse:    { name: "Wettbewerbs-Barometer™",     url: "/wettbewerb",   emoji: "/manus-storage/41_3269a7aa.svg" },
  website_realitaetscheck:   { name: "Website Realitäts-Check™",    url: "/website-realitaetscheck",    emoji: "/manus-storage/32_04885248.svg" },
  marketing_realitaetscheck: { name: "Marketing Realitäts-Check™",  url: "/marketing-realitaetscheck",  emoji: "/manus-storage/32_04885248.svg" },
  komplett_paket:        { name: "Komplett Website-RealitätsCheck™",            url: "/",             emoji: "/manus-storage/icon_onpage_grundlagen_a1ddd99a.png" },
  google_ads_potenzialanalyse: { name: "Google Ads Potenzialanalyse™", url: "/google-ads", emoji: "/manus-storage/47_7e4350f7.svg" },
};

interface PurchasedToken {
  token: string;
  used: boolean;
  productId: string | null;
}

export default function KaufErfolgreich() {
  useEffect(() => applySeo({
    title: "Kauf erfolgreich – Deine Analyse startet jetzt | WERBEAGENTUR Larissa Girenko",
    description: "Vielen Dank für deinen Kauf. Deine Analyse wird jetzt vorbereitet.",
    path: "/kauf-erfolgreich",
    noindex: true,
  }), []);
  const [location] = useLocation();
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const [tokens, setTokens] = useState<PurchasedToken[]>([]);
  const [polling, setPolling] = useState(true);
  const [pollCount, setPollCount] = useState(0);

  // Alle Tokens der Session abrufen (Polling bis Tokens da sind)
  const { data: tokenData, refetch } = trpc.payment.getTokensBySession.useQuery(
    { sessionId: sessionId ?? "" },
    { enabled: !!sessionId && polling, refetchInterval: false }
  );

  useEffect(() => {
    if (!sessionId) { setPolling(false); return; }

    const poll = async () => {
      for (let i = 0; i < 20; i++) {
        setPollCount(i + 1);
        const result = await refetch();
        if (result.data?.tokens && result.data.tokens.length > 0) {
          setTokens(result.data.tokens as PurchasedToken[]);
          setPolling(false);
          return;
        }
        await new Promise(r => setTimeout(r, 2000));
      }
      setPolling(false); // Timeout nach 40 Sekunden
    };

    poll();
  }, [sessionId]);

  useEffect(() => {
    if (tokenData?.tokens && tokenData.tokens.length > 0) {
      setTokens(tokenData.tokens as PurchasedToken[]);
      setPolling(false);
    }
  }, [tokenData]);

  const purchasedProducts = tokens
    .map(t => ({ ...t, info: t.productId ? PRODUCT_MAP[t.productId] : null }))
    .filter(t => t.info);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav />

      <main className="max-w-2xl mx-auto px-6 py-16">
        {/* Erfolgs-Header */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-4">✓</div>
          <h1 className="font-black uppercase text-black mb-3"
            style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 5vw, 3rem)" }}>
            Zahlung erfolgreich!
          </h1>
          <p className="text-gray-600 text-sm leading-relaxed max-w-md mx-auto">
            Vielen Dank für deinen Kauf! Larissa wird deine Website persönlich analysieren und dir das Ergebnis innerhalb von <strong className="text-black">72 Stunden</strong> per E-Mail zusenden.
          </p>
        </div>

        {/* 72h-Hinweis-Box */}
        <div className="border border-[#6d0000]/20 bg-[#6d0000]/5 p-5 mb-8">
          <div className="flex items-start gap-3">
            <span className="text-2xl flex-shrink-0">📬</span>
            <div>
              <p className="font-bold text-black text-sm mb-1">So läuft deine persönliche Analyse ab:</p>
              <ol className="space-y-1 text-gray-600 text-xs leading-relaxed list-decimal list-inside">
                <li>Larissa erhält sofort eine Benachrichtigung über deinen Auftrag.</li>
                <li>Sie analysiert deine Website manuell und persönlich — kein vollautomatischer Report.</li>
                <li>Du erhältst das Ergebnis als PDF-Report per E-Mail innerhalb von <strong>72 Stunden</strong>.</li>
              </ol>
              <p className="mt-2 text-gray-500 text-xs">Fragen? Schreib uns: <a href="mailto:hallo@werbeagentur.me" className="text-[#6d0000] underline">hallo@werbeagentur.me</a></p>
            </div>
          </div>
        </div>

        {/* Token-Polling Status */}
        {polling && (
          <div className="border border-gray-200 p-6 mb-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="w-5 h-5 border-2 border-[#6d0000] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm font-semibold text-gray-700">Zugänge werden aktiviert…</span>
            </div>
            <p className="text-xs text-gray-400">
              Stripe-Bestätigung wird verarbeitet ({pollCount}/20)
            </p>
          </div>
        )}

        {/* Gekaufte Produkte */}
        {!polling && purchasedProducts.length > 0 && (
          <div className="mb-10">
            <h2 className="font-bold text-sm uppercase tracking-widest text-gray-500 mb-4">
              Deine gekauften Analysen
            </h2>
            <div className="space-y-3">
              {purchasedProducts.map((p) => (
                <div key={p.token} className="border border-gray-200 p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <img src={p.info!.emoji} alt="" className="w-7 h-7 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                    <div>
                      <p className="font-bold text-sm text-black">{p.info!.name}</p>
                      <p className="text-xs text-gray-500">
                        Zugangs-Token: <code className="bg-gray-100 px-1 py-0.5 text-xs">{p.token.slice(0, 8)}…</code>
                      </p>
                    </div>
                  </div>
                  <a
                    href={`${p.info!.url}?token=${p.token}`}
                    className="inline-block text-white text-xs font-bold uppercase tracking-widest px-5 py-2.5 transition-opacity hover:opacity-90 whitespace-nowrap"
                    style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}
                  >
                    Jetzt analysieren →
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timeout / Kein Token */}
        {!polling && purchasedProducts.length === 0 && sessionId && (
          <div className="border border-amber-200 bg-amber-50 p-6 mb-8">
            <p className="font-bold text-sm text-amber-800 mb-2">⏳ Aktivierung dauert etwas länger</p>
            <p className="text-xs text-amber-700 leading-relaxed">
              Deine Zahlung wurde erfolgreich verarbeitet, aber die Aktivierung der Zugänge dauert noch einen Moment.
              Bitte lade die Seite in 30 Sekunden neu oder kontaktiere uns mit deiner Stripe-Session-ID:
            </p>
            <code className="block mt-2 text-xs bg-amber-100 px-2 py-1 text-amber-800 break-all">{sessionId}</code>
          </div>
        )}

        {/* Kein Session-ID */}
        {!sessionId && (
          <div className="border border-gray-200 p-6 mb-8 text-center">
            <p className="text-gray-600 text-sm">
              Kein Kauf-Parameter gefunden. Wenn du gerade etwas gekauft hast, prüfe deine E-Mails oder
              gehe zurück zur <a href="/" className="text-[#6d0000] font-bold underline">Startseite</a>.
            </p>
          </div>
        )}

        {/* Nächste Schritte */}
        <div className="border-t border-gray-100 pt-8">
          <h2 className="font-bold text-sm uppercase tracking-widest text-gray-500 mb-5">
            Wie geht es weiter?
          </h2>
          <ol className="space-y-4">
            {[
              { n: "1", title: "Larissa analysiert deine Website", text: "Larissa erhält deinen Auftrag und führt die Analyse persönlich durch — keine KI-Automatik, sondern echte Expertise." },
              { n: "2", title: "PDF-Report per E-Mail (72h)", text: "Du erhältst deinen vollständigen Barometer-Report als PDF innerhalb von 72 Stunden an deine Kauf-E-Mail." },
              { n: "3", title: "Befunde verstehen", text: "Jeder Befund ist in verständlicher Sprache erklärt — mit konkreten Maßnahmen, priorisiert nach Wirkung." },
              { n: "4", title: "Umsetzen & messen", text: "Setze die Empfehlungen um. Bei Fragen steht Larissa für ein persönliches Gespräch zur Verfügung." },
            ].map(step => (
              <li key={step.n} className="flex gap-4">
                <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-white text-xs font-black"
                  style={{ backgroundColor: "#6d0000", fontFamily: "'Bebas Neue', sans-serif" }}>
                  {step.n}
                </span>
                <div>
                  <p className="font-bold text-sm text-black">{step.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Zurück zur Startseite */}
        <div className="mt-10 text-center">
          <a href="/" className="text-sm text-gray-500 underline hover:text-black transition-colors">
            ← Zurück zur Startseite
          </a>
        </div>
      </main>
    </div>
  );
}
